/* eslint-disable hydrogen/prefer-image-component */
import {ReactElement, ReactNode, useCallback, useEffect, useMemo} from 'react';
import {ClientAnalytics, useUrl} from '@shopify/hydrogen';

import ProductTile from './ProductTile.client';
import CursorPaginationLink from './CursorPaginationLink.client';
import classNames from 'classnames';
import {useUrlStateParam} from '../hooks/index';
import {
  PAGINATION_PARAMS,
  SHOP_ORDER_OPTIONS,
  TwlAnalyticsEvent,
} from '../constants';
import {Product} from '@shopify/hydrogen/storefront-api-types';
import {getFilterParamsFromHref} from '../utils/shopFilterHelpers';
import {useAuthService} from '../contexts/AuthServiceProvider.client';
import {ShopPromoTileType} from '../types';

interface ShopProductListProps {
  products?: Array<Product>;
  previousCursor?: string;
  nextCursor?: string;
  header?: ReactNode;
  promos?: Array<ShopPromoTileType>;
}

export default function ShopProductList({
  products,
  promos,
  previousCursor,
  nextCursor,
  header,
}: ShopProductListProps): ReactElement {
  const {href} = useUrl();

  const {authUser} = useAuthService();

  const hidePromos = authUser?.member?.is_rewards_member ?? false;

  const items = useMemo(() => {
    if (hidePromos || !promos || promos.length <= 0 || products.length <= 0) {
      return products;
    }
    const itemsWithPromos: (Product | ShopPromoTileType)[] = [...products];
    for (const promo of promos) {
      itemsWithPromos.splice(
        Math.min(promo.position, itemsWithPromos.length) - 1,
        0,
        promo,
      );
    }
    return itemsWithPromos;
  }, [hidePromos, products, promos]);

  const getNavLink = useCallback(
    (before = true) => {
      const newUrl = new URL(href);
      newUrl.searchParams.delete(
        before
          ? PAGINATION_PARAMS.cursor.after
          : PAGINATION_PARAMS.cursor.before,
      );
      newUrl.searchParams.set(
        before
          ? PAGINATION_PARAMS.cursor.before
          : PAGINATION_PARAMS.cursor.after,
        before ? previousCursor : nextCursor,
      );
      return newUrl.href;
    },
    [href, previousCursor, nextCursor],
  );

  const {
    value: order,
    setValue: setOrder,
    unsetValue: unsetOrder,
  } = useUrlStateParam({param: 'o', defaultValue: 'default'});

  const handleOrderChange = useCallback(
    (e) => {
      const newUrl = new URL(href);
      // reset pagination whenever ordering is updated
      newUrl.searchParams.delete(PAGINATION_PARAMS.cursor.after);
      newUrl.searchParams.delete(PAGINATION_PARAMS.cursor.before);
      const queryValue = SHOP_ORDER_OPTIONS.get(
        e.currentTarget.value,
      )?.queryValue;
      if (queryValue) {
        // set ordering param to selected value
        setOrder(e.currentTarget.value, {href: newUrl.href});
      } else {
        // reset ordering to default ordering
        unsetOrder({href: newUrl.href});
      }
    },
    [setOrder, unsetOrder, href],
  );

  useEffect(
    () =>
      ClientAnalytics.publish(TwlAnalyticsEvent.SHOP_SEARCH, true, {
        ...ClientAnalytics.getPageAnalyticsData(),
        filters: getFilterParamsFromHref(href).filters,
      }),
    [href],
  );

  return (
    <div className="shop-products">
      {header}
      <div className="products__top filters__top">
        <div>
          Results found:{' '}
          {`${products.length}${nextCursor || previousCursor ? '+' : ''}`}
        </div>
        {products.length > 0 && (
          <div className="filters__top-sort">
            <select
              className="select-no-border pl-5"
              id="sort-bars-list"
              value={order}
              onChange={handleOrderChange}
            >
              {Array.from(SHOP_ORDER_OPTIONS.entries()).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {products.length === 0 && (
        <div className="no-items-yet">No products found.</div>
      )}
      <div className="shop-products__list products-list">
        {products.length > 0 && (
          <>
            {items.map((product) =>
              isProduct(product) ? (
                <ProductTile key={product.id} product={product} />
              ) : (
                <ShopPromoTile key={product.uuid} shopPromoTile={product} />
              ),
            )}
            <div
              className={classNames('pagination', {
                hide: !(previousCursor || nextCursor),
              })}
            >
              <CursorPaginationLink to={getNavLink()} cursor={previousCursor}>
                <i className="icon-left-arrow" />
                <span>&nbsp;Previous</span>
              </CursorPaginationLink>

              <CursorPaginationLink to={getNavLink(false)} cursor={nextCursor}>
                <span>Next&nbsp;</span>
                <i className="icon-right-arrow" />
              </CursorPaginationLink>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function isProduct(maybeProduct: unknown): maybeProduct is Product {
  return Object.hasOwnProperty.call(maybeProduct, 'id');
}

interface ShopPromoTileProps {
  shopPromoTile: ShopPromoTileType;
}
function ShopPromoTile({shopPromoTile}: ShopPromoTileProps) {
  return (
    <div className="product js-product">
      <a
        href={shopPromoTile.tile_url}
        className="product__promo"
        style={{
          backgroundColor: shopPromoTile.background_color,
          color: shopPromoTile.font_color,
        }}
      >
        <h4 className="product__promo__subtitle">{shopPromoTile.subtitle}</h4>
        <h3 className="product__promo__title">{shopPromoTile.main_title}</h3>
        <span className="product__promo__button">
          {shopPromoTile.button_text} <span className="icon-right-arrow1" />
        </span>
        <picture>
          {shopPromoTile.image_webp && (
            <source srcSet={shopPromoTile.image_webp} type="image/webp" />
          )}
          <source srcSet={shopPromoTile.image_png} type="image/jpeg" />
          <img src={shopPromoTile.image_png} alt="" />
        </picture>
      </a>
    </div>
  );
}

/* eslint-enable hydrogen/prefer-image-component */
